/**
 * Lo reciente de verde en todos los teléfonos de la estación (docs/esquema.md §5.5 y §12.11;
 * pedido de Agata del 5/10/2026: que Carlitos vea lo que cargó Héctor). El servidor baja a un
 * teléfono de verde `verde_reciente`: los viajes y descartes vigentes con hora declarada dentro de
 * una ventana que ÉL fija —las últimas 24 horas más una hora de tolerancia hacia adelante—, y acá
 * se funden por identidad con lo propio del teléfono (recibos que el snapshot todavía no refleja
 * y pendientes), con los bordes del snapshot: el teléfono no calcula ninguno con su reloj.
 */

import { entraElPropio, marcaDePendiente, ordenDelAcuse, ordenarRecientes, personasDe, ubicarEnVentana, type FilaBase } from './reciente';

// La ventana y la tolerancia viven en `reciente.ts` desde el 20/10 (§12.20, producción las comparte); siguen saliendo de acá.
export { TOLERANCIA_RECIENTE_MS, VENTANA_RECIENTE_MS, ventanaReciente } from './reciente';

export interface CarroReciente {
	carro_id: string;
	numero: number;
}

export interface ViajeReciente {
	raiz_id: string;
	version_id: string;
	campania_id: string;
	nro_viaje: number | null;
	fecha: string;
	entrada_en: string;
	lote_campo_id: string;
	lote: string;
	carros: CarroReciente[];
	cant_carros: number;
	bruto_g: number;
	neto_g: number;
	persona_id: string;
	persona2_id: string | null;
	dispositivo_id: string;
	/** 1 si esta versión corrige a otra (`corrige_a IS NOT NULL`): el teléfono no corrige, así que es «corregido por la oficina». */
	corregido: number;
}

export interface DescarteReciente {
	raiz_id: string;
	version_id: string;
	campania_id: string;
	fecha: string;
	registrado_en: string;
	neto_g: number;
	persona_id: string;
	/** 0004 (§12.13): la segunda persona que lo llevó, o null. */
	persona2_id: string | null;
	dispositivo_id: string;
	corregido: number;
}

export interface VerdeReciente {
	/** `YYYY-MM-DDTHH:MM` en hora del negocio: el instante del servidor menos 24 h. */
	desde: string;
	/** El instante del servidor más la tolerancia. */
	hasta: string;
	viajes: ViajeReciente[];
	descartes: DescarteReciente[];
}

/**
 * Lo propio de este teléfono, como lo entrega la cola: un recibo (ya en la oficina) o una fila
 * pendiente de verde. `hora` es la hora del hecho (`entrada_en` / `registrado_en`); un recibo
 * guardado por una versión anterior puede no tenerla, y entonces ordena por `sincronizado_en`.
 */
export interface RegistroPropio {
	entidad: 'viaje_verde' | 'descarte';
	/** La raíz que trajo el acuse; null en una pendiente, que todavía no tiene. */
	raiz_id: string | null;
	registro_id: string;
	hora: string | null;
	/** Solo en un recibo: cuándo lo acusó el servidor (ISO UTC). */
	sincronizado_en?: string;
	nro_viaje: number | null;
	personas: string[];
	lote: string | null;
	carros: string;
	cant_carros: number;
	neto_g: number;
	pendiente: boolean;
	/** De una pendiente: su id de outbox y su epoch, para la nota de «esperando señal». */
	id?: string;
	epoch?: string;
}

export interface FilaReciente extends FilaBase {
	entidad: 'viaje_verde' | 'descarte';
	nro_viaje: number | null;
	lote: string | null;
	carros: string;
	cant_carros: number;
	neto_g: number;
}

export interface TotalesRecientes {
	viajes: number;
	carros: number;
	neto_viajes_g: number;
	descartes: number;
	neto_descarte_g: number;
}

export interface ProyeccionReciente {
	filas: FilaReciente[];
	/** Sobre todas las filas; con un filtro por persona, la pantalla recalcula sobre las visibles (`totalesDe`). */
	totales: TotalesRecientes;
	/** Las personas que aparecen, en orden de aparición. */
	personas: string[];
	/** Lo propio fechado después de `hasta`: no se lista ni se suma; la pantalla lo cuenta aparte. */
	fuera_de_rango: number;
}

function textoDeCarros(carros: CarroReciente[]): string {
	return carros.map((c) => `N° ${c.numero}`).join(' + ');
}

/**
 * La fusión (§5.7 r. 21 aplicada a verde):
 * 0. nada fuera de `[desde, hasta]` entra, venga de donde venga; lo propio después de `hasta` se cuenta aparte;
 * 1. una fila del catálogo entra tal cual (el servidor ya la recortó con los mismos bordes);
 * 2. un recibo propio entra solo si su raíz no está en el catálogo Y es más nuevo que el snapshot
 *    (con la marca ausente, entra): si la raíz está, el catálogo trae la versión vigente, que
 *    puede ser una corrección de gestión; si no está y es más viejo, gestión lo anuló;
 * 3. una pendiente entra siempre que esté en la ventana;
 * 4. orden por la hora declarada, descendente; un recibo sin hora, por su acuse, después.
 */
export function proyectarVerdeReciente(
	bloque: VerdeReciente,
	propios: RegistroPropio[],
	horaDelCatalogo: string | undefined,
	dispositivoId: string | null,
): ProyeccionReciente {
	const { desde, hasta } = bloque;
	const enVentana = (hora: string) => hora >= desde && hora <= hasta;
	const raices = new Set<string>([...bloque.viajes.map((v) => v.raiz_id), ...bloque.descartes.map((d) => d.raiz_id)]);
	const filas: FilaReciente[] = [];

	for (const v of bloque.viajes) {
		if (!enVentana(v.entrada_en)) continue;
		filas.push({
			clave: `catalogo:viaje_verde:${v.raiz_id}`,
			entidad: 'viaje_verde',
			hora: v.entrada_en,
			orden: v.entrada_en,
			nro_viaje: v.nro_viaje,
			lote: v.lote,
			carros: textoDeCarros(v.carros),
			cant_carros: v.cant_carros,
			neto_g: v.neto_g,
			personas: v.persona2_id === null ? [v.persona_id] : [v.persona_id, v.persona2_id],
			este_telefono: dispositivoId !== null && v.dispositivo_id === dispositivoId,
			origen: 'catalogo',
			corregido: v.corregido === 1,
		});
	}
	for (const d of bloque.descartes) {
		if (!enVentana(d.registrado_en)) continue;
		filas.push({
			clave: `catalogo:descarte:${d.raiz_id}`,
			entidad: 'descarte',
			hora: d.registrado_en,
			orden: d.registrado_en,
			nro_viaje: null,
			lote: null,
			carros: '',
			cant_carros: 0,
			neto_g: d.neto_g,
			personas: d.persona2_id === null ? [d.persona_id] : [d.persona_id, d.persona2_id],
			este_telefono: dispositivoId !== null && d.dispositivo_id === dispositivoId,
			origen: 'catalogo',
			corregido: d.corregido === 1,
		});
	}

	let fueraDeRango = 0;
	for (const p of propios) {
		const orden = p.hora ?? ordenDelAcuse(p.sincronizado_en);
		const donde = ubicarEnVentana(orden, desde, hasta);
		if (donde === 'despues') {
			fueraDeRango += 1;
			continue;
		}
		if (donde === 'antes' || !entraElPropio(p, raices, horaDelCatalogo)) continue;
		filas.push({
			clave: `${p.pendiente ? 'pendiente' : 'recibo'}:${p.entidad}:${p.registro_id}`,
			entidad: p.entidad,
			hora: p.hora,
			orden,
			nro_viaje: p.nro_viaje,
			lote: p.lote,
			carros: p.carros,
			cant_carros: p.cant_carros,
			neto_g: p.neto_g,
			personas: p.personas,
			este_telefono: true,
			origen: p.pendiente ? 'pendiente' : 'recibo',
			corregido: false,
			...marcaDePendiente(p),
		});
	}

	ordenarRecientes(filas);
	return { filas, totales: totalesDe(filas), personas: personasDe(filas), fuera_de_rango: fueraDeRango };
}

/**
 * Viajes, carros y kilos de un conjunto de filas: el ticket de la pantalla se calcula sobre las filas que se
 * ven, así con una persona elegida dice lo de esa persona y no lo de la estación (Codex, ronda del diff, 5/10).
 */
export function totalesDe(filas: FilaReciente[]): TotalesRecientes {
	const totales: TotalesRecientes = { viajes: 0, carros: 0, neto_viajes_g: 0, descartes: 0, neto_descarte_g: 0 };
	for (const f of filas) {
		if (f.entidad === 'viaje_verde') {
			totales.viajes += 1;
			totales.carros += f.cant_carros;
			totales.neto_viajes_g += f.neto_g;
		} else {
			totales.descartes += 1;
			totales.neto_descarte_g += f.neto_g;
		}
	}
	return totales;
}
